/**
 * consentCopy.ts — the single source of the assistant consent language.
 *
 * The consent body is the text a user reads before agreeing that their recipe
 * text may be sent to a language model. It is the lawful basis for the whole
 * assistant surface (DPIA delta §5,
 * `.plan/legal/2026-07-dpia-workers-ai-assistant.md`), so it must read
 * IDENTICALLY wherever consent is captured. Two surfaces capture it today:
 *
 *   - Settings → Assistant (`../settings/AssistantSection.tsx`) — the canonical
 *     manage / withdraw surface.
 *   - The inline consent card (`./AssistantConsentCard.tsx`) — shown when the
 *     user initiates an assistant action while opted out.
 *
 * Both import from here, and `consentCopy.test.ts` asserts the rendered strings
 * are byte-identical. Editing the body is a legal change, not a copy tweak: the
 * DPIA delta describes this text, so an edit needs the same review the original
 * had (voice-copy-reviewer + a DPIA amendment).
 *
 * Voice: branding.md §5 — precise, quiet, unsentimental; §5.2.1 — no
 * exclamations, no banned words, never "our AI" / "smart" / "magic".
 */

/**
 * What leaves the account when the assistant is on — stated plainly, aligned to
 * the DPIA delta's processing inventory (§2) and lawful-basis section (§5).
 */
export const ASSISTANT_CONSENT_BODY =
  "When the assistant is on, the recipe text you hand it — text you paste to convert, the draft you run an operation on, and your library settings — is sent to a language model run by Cloudflare to produce the result. It stays off until you turn it on, nothing leaves your account before then, and only the recipe you run it on is sent. Turn it off at any time.";

/**
 * Rule 8 failure line for a consent save. Shared so the two surfaces cannot
 * drift into two different accounts of the same failure.
 */
export const ASSISTANT_CONSENT_SAVE_ERROR =
  "Couldn't save that change. Check your connection and try again.";

/**
 * Shown when the consent state is unavailable (still loading, or a read
 * failure). Never a fabricated value — the affordance stays inert.
 */
export const ASSISTANT_CONSENT_UNAVAILABLE =
  "This setting is unavailable right now. Try again shortly.";
